'use client';

import type { CountryOption } from '@/lib/waitlist/countries';
import {
  NG_CITY_SUGGESTIONS,
  NG_STATES,
  NG_STATE_CODES,
  ROLE_LABELS,
  SERVICE_LABELS,
  WAITLIST_ROLE_IDS,
  WAITLIST_SERVICES,
  type WaitlistFieldErrors,
  type WaitlistRoleId,
  type WaitlistService,
} from '@/lib/waitlist/schema';
import { errorClass, hintClass, inputClass, labelClass } from './ui';

/**
 * The details a member can give and later change: name, where they are, who
 * they are and what they would use Point2 for, plus the separate product
 * updates choice. Used by the join form and the manage view, controlled by
 * them, so input survives every recoverable error.
 */

export interface DetailsValues {
  displayName: string;
  countryCode: string;
  region: string;
  city: string;
  roles: WaitlistRoleId[];
  services: WaitlistService[];
  marketing: boolean;
}

/** "Just curious" stands alone: choosing it clears the others, and choosing another clears it. */
export function toggleRole(roles: WaitlistRoleId[], id: WaitlistRoleId, checked: boolean): WaitlistRoleId[] {
  if (!checked) return roles.filter((r) => r !== id);
  if (id === 'CURIOUS') return ['CURIOUS'];
  return [...roles.filter((r) => r !== 'CURIOUS' && r !== id), id];
}

export const fieldId = (prefix: string, name: string) => `${prefix}-${name}`;
export const errorId = (prefix: string, name: string) => `${prefix}-${name}-error`;

const tileClass =
  'flex min-h-12 cursor-pointer items-center gap-3 rounded-lg border border-grey-300 bg-white px-4 py-2.5 text-[15px] text-grey-900 transition-colors hover:border-grey-400 has-[:checked]:border-brand-600 has-[:checked]:bg-brand-25 has-[:focus-visible]:ring-3 has-[:focus-visible]:ring-brand-600/25';
const checkboxClass = 'size-5 shrink-0 accent-brand-600 outline-none';

export default function DetailsFields({
  prefix,
  values,
  errors,
  countries,
  onChange,
  disabled = false,
}: {
  prefix: string;
  values: DetailsValues;
  errors: WaitlistFieldErrors;
  countries: CountryOption[];
  onChange: (next: DetailsValues) => void;
  disabled?: boolean;
}) {
  const set = <K extends keyof DetailsValues>(key: K, value: DetailsValues[K]) => onChange({ ...values, [key]: value });
  const described = (name: keyof WaitlistFieldErrors, hint?: string) =>
    [hint, errors[name] ? errorId(prefix, name) : undefined].filter(Boolean).join(' ') || undefined;
  const fieldError = (name: keyof WaitlistFieldErrors) =>
    errors[name] ? (
      <p id={errorId(prefix, name)} className={errorClass}>
        {errors[name]}
      </p>
    ) : null;
  const isNg = values.countryCode === 'NG';
  const wantsServices = values.roles.includes('CUSTOMER');

  return (
    <>
      <div className="flex flex-col gap-2">
        <label htmlFor={fieldId(prefix, 'displayName')} className={labelClass}>
          What should we call you? <span className="font-normal text-grey-600">(optional)</span>
        </label>
        <input
          id={fieldId(prefix, 'displayName')}
          name="displayName"
          type="text"
          autoComplete="name"
          maxLength={80}
          value={values.displayName}
          onChange={(e) => set('displayName', e.target.value)}
          aria-invalid={errors.displayName ? true : undefined}
          aria-describedby={described('displayName')}
          disabled={disabled}
          className={inputClass}
        />
        {fieldError('displayName')}
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <label htmlFor={fieldId(prefix, 'countryCode')} className={labelClass}>
            Country
          </label>
          <select
            id={fieldId(prefix, 'countryCode')}
            name="countryCode"
            autoComplete="country"
            value={values.countryCode}
            onChange={(e) => onChange({ ...values, countryCode: e.target.value, region: e.target.value === 'NG' ? values.region : '' })}
            aria-invalid={errors.countryCode ? true : undefined}
            aria-describedby={described('countryCode')}
            disabled={disabled}
            className={`${inputClass} appearance-auto`}
          >
            {countries.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name}
              </option>
            ))}
          </select>
          {fieldError('countryCode')}
        </div>

        {isNg ? (
          <div className="flex flex-col gap-2">
            <label htmlFor={fieldId(prefix, 'region')} className={labelClass}>
              State
            </label>
            <select
              id={fieldId(prefix, 'region')}
              name="region"
              value={values.region}
              onChange={(e) => set('region', e.target.value)}
              aria-invalid={errors.region ? true : undefined}
              aria-describedby={described('region')}
              disabled={disabled}
              className={`${inputClass} appearance-auto`}
            >
              <option value="">Choose your state</option>
              {NG_STATE_CODES.map((code) => (
                <option key={code} value={code}>
                  {NG_STATES[code]}
                </option>
              ))}
            </select>
            {fieldError('region')}
          </div>
        ) : null}
      </div>

      <div className="flex flex-col gap-2">
        <label htmlFor={fieldId(prefix, 'city')} className={labelClass}>
          City or town <span className="font-normal text-grey-600">(optional)</span>
        </label>
        <input
          id={fieldId(prefix, 'city')}
          name="city"
          type="text"
          autoComplete="address-level2"
          maxLength={80}
          list={isNg ? fieldId(prefix, 'city-list') : undefined}
          value={values.city}
          onChange={(e) => set('city', e.target.value)}
          aria-invalid={errors.city ? true : undefined}
          aria-describedby={described('city', isNg ? fieldId(prefix, 'city-hint') : undefined)}
          disabled={disabled}
          className={inputClass}
        />
        {isNg ? (
          <>
            <p id={fieldId(prefix, 'city-hint')} className={hintClass}>
              Not listed? Type your city.
            </p>
            <datalist id={fieldId(prefix, 'city-list')}>
              {NG_CITY_SUGGESTIONS.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </>
        ) : null}
        {fieldError('city')}
      </div>

      <fieldset className="flex flex-col gap-2" aria-describedby={described('roles', fieldId(prefix, 'roles-hint'))} disabled={disabled}>
        <legend className={`${labelClass} mb-1`}>I&apos;m interested as a…</legend>
        <p id={fieldId(prefix, 'roles-hint')} className={`${hintClass} -mt-1 mb-1`}>
          Choose all that apply.
        </p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {WAITLIST_ROLE_IDS.map((r, i) => (
            <label key={r} className={tileClass}>
              <input
                // The first box is where the summary sends focus for a roles error.
                id={i === 0 ? fieldId(prefix, 'roles') : undefined}
                type="checkbox"
                name="roles"
                value={r}
                checked={values.roles.includes(r)}
                onChange={(e) => set('roles', toggleRole(values.roles, r, e.target.checked))}
                aria-invalid={errors.roles ? true : undefined}
                className={checkboxClass}
              />
              {ROLE_LABELS[r]}
            </label>
          ))}
        </div>
        {fieldError('roles')}
      </fieldset>

      {wantsServices ? (
        <fieldset className="flex flex-col gap-2" aria-describedby={described('services')} disabled={disabled}>
          <legend className={`${labelClass} mb-1`}>
            What would you use Point2 for? <span className="font-normal text-grey-600">(optional)</span>
          </legend>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {WAITLIST_SERVICES.map((s, i) => (
              <label key={s} className={tileClass}>
                <input
                  id={i === 0 ? fieldId(prefix, 'services') : undefined}
                  type="checkbox"
                  name="services"
                  value={s}
                  checked={values.services.includes(s)}
                  onChange={(e) =>
                    set('services', e.target.checked ? [...values.services.filter((x) => x !== s), s] : values.services.filter((x) => x !== s))
                  }
                  className={checkboxClass}
                />
                {SERVICE_LABELS[s]}
              </label>
            ))}
          </div>
          {fieldError('services')}
        </fieldset>
      ) : null}

      <div className="flex flex-col gap-2">
        <label className="flex min-h-11 cursor-pointer items-start gap-3 text-[15px] text-grey-800">
          <input
            id={fieldId(prefix, 'marketing')}
            type="checkbox"
            name="marketing"
            checked={values.marketing}
            onChange={(e) => set('marketing', e.target.checked)}
            disabled={disabled}
            className={`${checkboxClass} mt-0.5 focus-visible:ring-3 focus-visible:ring-brand-600/25`}
          />
          <span>Also send me occasional Point2 product updates. You can stop these at any time.</span>
        </label>
        {fieldError('marketing')}
      </div>
    </>
  );
}

/** The fields as the gateway wants them for a join (blank text omitted later by the schema). */
export function detailsPayload(v: DetailsValues) {
  return {
    displayName: v.displayName,
    countryCode: v.countryCode,
    region: v.countryCode === 'NG' ? v.region : '',
    city: v.city,
    roles: v.roles,
    // Services describe what a customer would book; without that role they mean nothing.
    services: v.roles.includes('CUSTOMER') ? v.services : [],
    marketing: v.marketing,
  };
}
